// @ts-expect-error
import { FixedSizeList } from "react-window";
import {
  Checkbox,
  ListItem,
  Typography,
  ListItemText,
  ListItemButton,
  Box,
} from "@mui/material";

import { HighlightLabel } from "../../highlight-label";

import { FilterBadge } from "../styled";
import React, { ReactNode } from "react";

export type FilterCheckboxListItem = {
  id: string;
  name: string;
  count?: number;
};

export type FilterCheckboxListItems<
  T extends FilterCheckboxListItem = FilterCheckboxListItem,
> = Array<T>;

export type FilterCheckboxListProps<T extends FilterCheckboxListItem> = {
  items?: FilterCheckboxListItems<T>;
  selectedItemIds?: string[];
  onSelectItem?: (itemId: string) => void;
  highlightValue?: string;
  adornment?: (props: { item: T }) => ReactNode;
};

export function FilterCheckboxList<T extends FilterCheckboxListItem>({
  items,
  selectedItemIds,
  onSelectItem,
  highlightValue,
  adornment,
}: FilterCheckboxListProps<T>) {
  if (!items || !items.length) {
    return "No options available";
  }

  const handleSelectItem = (itemId: string) => {
    if (!onSelectItem) return;
    onSelectItem(itemId);
  };

  let height = 29 * items.length;
  if (height > 350) height = 350;

  const allItemsSortedByName = [...items].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const [selectedItems, unselectedItems] = allItemsSortedByName.reduce<
    [Array<T>, Array<T>]
  >(
    (acc, curr) => {
      if (selectedItemIds?.includes(curr.id)) {
        acc[0].push(curr);
      } else {
        acc[1].push(curr);
      }
      return acc;
    },
    [[], []],
  );

  const allItemsSortedBySelected = [...selectedItems, ...unselectedItems];

  return (
    <FixedSizeList
      height={height}
      width="100%"
      itemSize={29}
      overscanCount={5}
      itemData={{ items: allItemsSortedBySelected }}
      itemCount={allItemsSortedBySelected.length}
    >
	  {/* @ts-ignore */}
      {({ style, index, data }) => {
        return (
          <ListItem
            dense
            disablePadding
            style={style}
            secondaryAction={
              <FilterBadge badgeContent={data.items[index].count} />
            }
          >
            <ListItemButton
              dense
              onClick={() => handleSelectItem(data.items[index].id)}
            >
              <Checkbox
                sx={{ p: 0, px: 1 }}
                edge="start"
                size="small"
                checked={selectedItemIds?.includes(data.items[index].id)}
              />
              <ListItemText sx={{ margin: 0 }}>
                <Box display={"flex"} alignItems="center">
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ display: "block", mr: 1 }}
                  >
                    <HighlightLabel
                      label={data.items[index].name}
                      highlight={highlightValue}
                    />
                  </Typography>
                  {adornment && adornment({ item: data.items[index] })}
                </Box>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        );
      }}
    </FixedSizeList>
  );
}
